import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Trash2 } from 'lucide-react';
import type { SelectionData } from '../types';

export const HIGHLIGHT_COLORS = [
  { name: 'yellow', value: '#fef08a', bg: 'rgba(254, 240, 138, 0.6)' },
  { name: 'green', value: '#bbf7d0', bg: 'rgba(187, 247, 208, 0.6)' },
  { name: 'blue', value: '#bfdbfe', bg: 'rgba(191, 219, 254, 0.6)' },
  { name: 'pink', value: '#fbcfe8', bg: 'rgba(251, 207, 232, 0.6)' },
  { name: 'orange', value: '#fed7aa', bg: 'rgba(254, 215, 170, 0.6)' },
] as const;

interface HighlightToolbarProps {
  selection: SelectionData | null;
  onHighlight: (color: string) => void;
  onAddNote?: () => void;
  onDelete?: () => void;
  onClose: () => void;
  theme: 'light' | 'dark' | 'sepia';
  isExistingHighlight?: boolean;
}

export default function HighlightToolbar({
  selection,
  onHighlight,
  onAddNote,
  onDelete,
  onClose,
  theme,
  isExistingHighlight = false,
}: HighlightToolbarProps) {
  const isDark = theme === 'dark';

  if (!selection) return null;

  // Responsive positioning
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const getPosition = () => {
    if (isMobile) {
      // Position at bottom of screen on mobile for easier thumb access
      return {
        position: 'fixed' as const,
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        top: 'auto',
      };
    }

    // Desktop: position above selection
    const toolbarTop = selection.rect.top - 56;
    const toolbarLeft = selection.rect.left + selection.rect.width / 2;

    return {
      position: 'fixed' as const,
      top: `${Math.max(toolbarTop, 70)}px`,
      left: `${Math.min(Math.max(toolbarLeft, 100), window.innerWidth - 100)}px`,
      transform: 'translateX(-50%)',
    };
  };

  return (
    <AnimatePresence>
      <motion.div
        data-highlight-toolbar
        initial={{ opacity: 0, y: isMobile ? 20 : 10, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: isMobile ? 20 : 10, scale: 0.9 }}
        style={{
          ...getPosition(),
          background: isDark ? '#2d2d3f' : '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 200,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        }}
      >
        {/* Color options */}
        {HIGHLIGHT_COLORS.map((color) => (
          <button
            key={color.name}
            onClick={() => onHighlight(color.name)}
            title={`Highlight ${color.name}`}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: color.value,
              border: '2px solid transparent',
              cursor: 'pointer',
              transition: 'transform 0.15s, border-color 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.15)';
              e.currentTarget.style.borderColor = 'var(--accent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          />
        ))}

        <div
          style={{
            width: '1px',
            height: '28px',
            background: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
            margin: '0 4px',
          }}
        />

        {/* Add note button */}
        {onAddNote && (
          <button
            onClick={onAddNote}
            title="Add note"
            style={{
              background: 'none',
              border: 'none',
              padding: '6px',
              cursor: 'pointer',
              color: isDark ? '#fff' : 'var(--text)',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '6px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <MessageSquare size={18} />
          </button>
        )}

        {/* Delete button (for existing highlights) */}
        {isExistingHighlight && onDelete && (
          <button
            onClick={onDelete}
            title="Remove highlight"
            style={{
              background: 'none',
              border: 'none',
              padding: '6px',
              cursor: 'pointer',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '6px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <Trash2 size={18} />
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          title="Cancel"
          style={{
            background: 'none',
            border: 'none',
            padding: '6px',
            cursor: 'pointer',
            color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '6px',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'none';
          }}
        >
          <X size={18} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}

// Helper function to apply highlights to HTML content
export function applyHighlightsToContent(
  content: string,
  highlights: Array<{
    id: string;
    startOffset: number;
    endOffset: number;
    color: string;
    note?: string;
  }>
): string {
  if (!highlights.length) return content;

  // Sort highlights by start position (descending to apply from end first)
  const sortedHighlights = [...highlights].sort(
    (a, b) => b.startOffset - a.startOffset
  );

  let result = content;

  for (const highlight of sortedHighlights) {
    const colorData = HIGHLIGHT_COLORS.find((c) => c.name === highlight.color);
    const bgColor = colorData?.bg || 'rgba(254, 240, 138, 0.6)';
    const hasNote = highlight.note ? 'has-note' : '';

    // Safely extract text at the offset positions
    const before = result.slice(0, highlight.startOffset);
    const text = result.slice(highlight.startOffset, highlight.endOffset);
    const after = result.slice(highlight.endOffset);

    result = `${before}<mark data-highlight-id="${highlight.id}" class="reader-highlight ${hasNote}" style="background-color: ${bgColor};">${text}</mark>${after}`;
  }

  return result;
}
